/**
 * The OKR-list URL contract: one serialiser, one parser, and the link builder that pairs them.
 *
 * These live here rather than inside `useOKRFilters` for a reason that is not tidiness. They
 * are pure functions, but importing them from the hook drags in `AuthContext` → `services` →
 * `api.ts`, which uses `import.meta.env` and cannot be loaded by Jest at all. So for as long
 * as this contract lived in the hook it was, in practice, untestable — and it broke twice
 * without anything noticing (see `okrListURL.test.ts`).
 *
 * Keep this module free of React and service imports so the contract stays testable.
 */

import type { OKRFilters } from '../types/okr';

/**
 * Parse filters out of the OKR list's URL search params.
 *
 * The inverse of `filtersToURLParams`. A param this function does not read is not an error —
 * it is silently ignored, which yields an UNFILTERED list. That is exactly how `?status=` and
 * `?department=` shipped: a plausible page answering the wrong question. Build links with
 * `buildOKRListURL` so a producer can never name a param this parser does not read.
 */
export const parseFiltersFromURL = (searchParams: URLSearchParams): Partial<OKRFilters> => {
  const filters: Partial<OKRFilters> = {};

  // Simple string params
  const search = searchParams.get('search');
  if (search) filters.search = search;

  const startDate = searchParams.get('startDate');
  if (startDate) filters.startDate = startDate;

  const endDate = searchParams.get('endDate');
  if (endDate) filters.endDate = endDate;

  const sortBy = searchParams.get('sortBy') as OKRFilters['sortBy'];
  if (sortBy) filters.sortBy = sortBy;

  const sortOrder = searchParams.get('sortOrder') as OKRFilters['sortOrder'];
  if (sortOrder) filters.sortOrder = sortOrder;

  const filterMode = searchParams.get('filterMode') as OKRFilters['filterMode'];
  if (filterMode) filters.filterMode = filterMode;

  // Number params
  const page = searchParams.get('page');
  if (page) filters.page = parseInt(page, 10);

  const limit = searchParams.get('limit');
  if (limit) filters.limit = parseInt(limit, 10);

  const progressMin = searchParams.get('progressMin');
  if (progressMin) filters.progressMin = parseInt(progressMin, 10);

  const progressMax = searchParams.get('progressMax');
  if (progressMax) filters.progressMax = parseInt(progressMax, 10);

  // Array params (comma-separated)
  const types = searchParams.get('types');
  if (types) filters.types = types.split(',').filter(Boolean);

  const statuses = searchParams.get('statuses');
  if (statuses) filters.statuses = statuses.split(',').filter(Boolean);

  const ownerIds = searchParams.get('ownerIds');
  if (ownerIds) filters.ownerIds = ownerIds.split(',').filter(Boolean);

  const departmentIds = searchParams.get('departmentIds');
  if (departmentIds) filters.departmentIds = departmentIds.split(',').filter(Boolean);

  const cycleIds = searchParams.get('cycleIds');
  if (cycleIds) filters.cycleIds = cycleIds.split(',').filter(Boolean);

  return filters;
};

/**
 * Convert filters to URL search params.
 *
 * Accepts a partial: every branch below is guarded, so callers building a one-filter
 * drill-down link pass just that filter.
 */
export const filtersToURLParams = (filters: Partial<OKRFilters>): URLSearchParams => {
  const params = new URLSearchParams();

  // Simple string params
  if (filters.search) params.set('search', filters.search);
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  if (filters.sortOrder) params.set('sortOrder', filters.sortOrder);
  if (filters.filterMode && filters.filterMode !== 'AND') params.set('filterMode', filters.filterMode);

  // Number params
  if (filters.page && filters.page !== 1) params.set('page', filters.page.toString());
  if (filters.limit && filters.limit !== 20) params.set('limit', filters.limit.toString());
  if (filters.progressMin !== undefined) params.set('progressMin', filters.progressMin.toString());
  if (filters.progressMax !== undefined) params.set('progressMax', filters.progressMax.toString());

  // Array params (comma-separated)
  if (filters.types && filters.types.length > 0) params.set('types', filters.types.join(','));
  if (filters.statuses && filters.statuses.length > 0) params.set('statuses', filters.statuses.join(','));
  if (filters.ownerIds && filters.ownerIds.length > 0) params.set('ownerIds', filters.ownerIds.join(','));
  if (filters.departmentIds && filters.departmentIds.length > 0) params.set('departmentIds', filters.departmentIds.join(','));
  if (filters.cycleIds && filters.cycleIds.length > 0) params.set('cycleIds', filters.cycleIds.join(','));

  return params;
};

/**
 * Build a link to the OKR list pre-filtered by `filters`.
 *
 * Use this for every drill-down instead of hand-writing a query string. Hand-written links are
 * how both drill-down defects happened: `?status=` and `?department=` are names
 * `parseFiltersFromURL` never reads, so the list rendered UNFILTERED and looked plausible.
 * Routing through `filtersToURLParams` makes the producer and the parser one contract.
 *
 * Note `departmentIds` holds `Department.id` uuids — the OKR list filters on the owner's
 * department memberships, the same as Reports, and answers a name with 422.
 */
export const buildOKRListURL = (filters: Partial<OKRFilters>): string => {
  const query = filtersToURLParams(filters).toString();
  return query ? `/okrs?${query}` : '/okrs';
};
